import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ChevronLeft, Shield, ShieldCheck, ShieldX, Wallet, Building2,
  Mail, Clock, Send, CheckCircle, AlertCircle, User, FileText, Download, FileUp,
  Trash2, Settings, Plus, UserPlus, X, LogIn
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { Card, CardContent } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';
import { toast } from 'sonner';
import { TENANT } from '../../config';
import { deleteUserConfirmText, personName } from './userDeletionCopy';

const fmtCurrency = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString() : '—';
const fmtDateTime = (d?: string) => d ? new Date(d).toLocaleString() : '—';

const statusLabel = (status?: string) => (status || 'pending_review')
  .split('_')
  .map(part => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const accessBadge = (status?: string) => {
  const normalized = status || 'pending_review';
  const cls = normalized === 'approved' || normalized === 'legacy_manual_review_approved' || normalized === 'documents_unlocked' || normalized === 'eligible_to_invest'
    ? 'bg-green-50 text-green-700'
    : normalized === 'needs_more_information' || normalized === 'pending_review' || normalized === 'investment_pending_compliance'
      ? 'bg-amber-50 text-amber-700'
      : normalized === 'rejected' || normalized === 'restricted' || normalized === 'suspended'
        ? 'bg-red-50 text-red-700'
        : 'bg-gray-50 text-gray-700';
  return <Badge variant="secondary" className={`text-xs ${cls}`}>{statusLabel(normalized)}</Badge>;
};

const kycBadge = (status: string) => {
  const map: Record<string, { cls: string; icon: any; label: string }> = {
    verified: { cls: 'bg-green-50 text-green-700', icon: ShieldCheck, label: 'Verified' },
    pending:  { cls: 'bg-amber-50 text-amber-700', icon: Shield, label: 'Pending' },
    rejected: { cls: 'bg-red-50 text-red-700', icon: ShieldX, label: 'Rejected' },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <Badge variant="secondary" className={`text-xs ${m.cls}`}>
      <Icon className="h-3 w-3 mr-1" />{m.label}
    </Badge>
  );
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userDetail, setUserDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const navigate = useNavigate();
  const { impersonateUser } = useAuth();
  const { loginAs } = useUser();
  const [sendingKyc, setSendingKyc] = useState(false);
  const [sendingAccreditation, setSendingAccreditation] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [accreditationDocs, setAccreditationDocs] = useState<any[]>([]);
  const [updatingAccredStatus, setUpdatingAccredStatus] = useState(false);
  const [updatingAccessReview, setUpdatingAccessReview] = useState(false);
  const [accessReviewForm, setAccessReviewForm] = useState({
    relationshipSource: '',
    knownBy: '',
    rationale: '',
    reviewNotes: '',
    complianceCaveat: '',
  });

  useEffect(() => {
    if (!userDetail?.id) return;
    setAccessReviewForm({
      relationshipSource: userDetail.relationship_source || '',
      knownBy: userDetail.known_by || '',
      rationale: userDetail.approval_rationale || '',
      reviewNotes: userDetail.review_notes || '',
      complianceCaveat: userDetail.compliance_caveat || 'Document access approval does not approve investment execution.',
    });
  }, [userDetail?.id, userDetail?.relationship_source, userDetail?.known_by, userDetail?.approval_rationale, userDetail?.review_notes, userDetail?.compliance_caveat]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const qp = new URLSearchParams({ page: String(page), limit: '50' });
      if (search) qp.set('search', search);
      if (roleFilter !== 'all') qp.set('role', roleFilter);
      if (kycFilter !== 'all') qp.set('kyc_status', kycFilter);
      const res: any = await api.get(`/admin/users?${qp.toString()}`);
      setUsers(res?.data || []);
      setPagination(res?.pagination || null);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, search, roleFilter, kycFilter]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (userId: string) => {
    setDetailLoading(true);
    try {
      const res: any = await api.get(`/admin/users/${userId}`);
      setUserDetail(res?.data || res);
    } catch (err) { console.error(err); toast.error('Failed to load user details'); }
    finally { setDetailLoading(false); }
  };

  const openUser = (u: any) => {
    setSelectedUser(u);
    loadDetail(u.id);
    loadAccreditationDocs(u.id);
  };

  const sendKycReminder = async (userId: string) => {
    setSendingKyc(true);
    try {
      const res: any = await api.post(`/admin/users/${userId}/send-kyc-reminder`);
      const d = res?.data || res;
      if (d.success) toast.success(d.message || 'KYC reminder sent');
      else toast.error(d.message || 'Failed to send reminder');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send reminder');
    } finally { setSendingKyc(false); }
  };

  const sendAccreditationNudge = async (userId: string) => {
    setSendingAccreditation(true);
    try {
      const res: any = await api.post(`/accreditation/admin/users/${userId}/nudge`);
      const d = res?.data || res;
      if (d.success) toast.success(d.message || 'Accreditation request sent');
      else toast.error(d.message || 'Failed to send request');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send accreditation request');
    } finally { setSendingAccreditation(false); }
  };

  const loadAccreditationDocs = async (userId: string) => {
    try {
      const res: any = await api.get(`/accreditation/admin/users/${userId}/docs`);
      setAccreditationDocs(res?.data?.documents || res?.documents || []);
    } catch { setAccreditationDocs([]); }
  };

  const updateAccreditationStatus = async (userId: string, status: string) => {
    setUpdatingAccredStatus(true);
    try {
      const res: any = await api.patch(`/accreditation/admin/users/${userId}/status`, { status });
      const d = res?.data || res;
      if (d.success) {
        toast.success(`Accreditation status updated to ${status}`);
        loadDetail(userId);
      } else toast.error(d.message || 'Failed to update status');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update status');
    } finally { setUpdatingAccredStatus(false); }
  };

  const submitInvestorAccessReview = async (decision: string) => {
    if (!userDetail?.id) return;
    setUpdatingAccessReview(true);
    try {
      const brandName = TENANT.branding?.name || 'the platform';
      const defaultRationale = decision === 'legacy_manual_review_approved'
        ? `Legacy manual review approved for offering-document access based on pre-existing warm relationship with ${brandName} leadership.`
        : decision === 'approve_document_access'
          ? 'Manual review approved for offering-document access. Investment eligibility remains separately tracked.'
          : decision === 'approve_investment'
            ? 'Final investment eligibility approved. All upstream gates (KYC, accreditation, document access, manual review) confirmed complete.'
            : accessReviewForm.rationale;
      const res: any = await api.post(`/admin/users/${userDetail.id}/investor-access-review`, {
        decision,
        relationshipSource: accessReviewForm.relationshipSource,
        knownBy: accessReviewForm.knownBy,
        rationale: accessReviewForm.rationale || defaultRationale,
        reviewNotes: accessReviewForm.reviewNotes,
        complianceCaveat: accessReviewForm.complianceCaveat,
      });
      const d = res?.data || res;
      if (d.success === false) {
        toast.error(d.message || 'Failed to update investor access');
        return;
      }
      toast.success('Investor access review updated');
      await loadDetail(userDetail.id);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update investor access');
    } finally {
      setUpdatingAccessReview(false);
    }
  };

  const [updatingRole, setUpdatingRole] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [updatingPermissions, setUpdatingPermissions] = useState(false);
  // The non-admin account this screen creates. An investing tenant creates
  // investors; eMerge has none, so it creates workforce members with the
  // legacy 'employee' role — the same role Jody's seeded account carries.
  // Label follows the tenant; the stored role value follows it too, because
  // labelling an 'investor' row "Employee" would be a lie the export repeats.
  const MEMBER_ROLE = TENANT.features.investing ? 'investor' : 'employee';
  const MEMBER_LABEL = TENANT.features.investing ? 'Investor' : 'Employee';
  const [showCreateModal, setShowCreateModal] = useState<'admin' | 'member' | null>(null);
  const [createForm, setCreateForm] = useState({ email: '', firstName: '', lastName: '', password: '', accreditationStatus: 'pending' });
  const [creating, setCreating] = useState(false);

  const createUser = async () => {
    if (!createForm.email.trim()) { toast.error('Email is required'); return; }
    setCreating(true);
    try {
      const res: any = await api.post('/admin/users', {
        email: createForm.email,
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        password: createForm.password || undefined,
        role: showCreateModal === 'admin' ? 'admin' : MEMBER_ROLE,
        accreditationStatus: showCreateModal === 'member' && TENANT.features.investing ? createForm.accreditationStatus : undefined,
      });
      const d = res?.data || res;
      if (d.success !== false) {
        toast.success(`${showCreateModal === 'admin' ? 'Admin' : MEMBER_LABEL} user created`);
        setShowCreateModal(null);
        setCreateForm({ email: '', firstName: '', lastName: '', password: '', accreditationStatus: 'pending' });
        load();
      } else toast.error(d.message || 'Failed to create user');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create user');
    } finally { setCreating(false); }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    setUpdatingRole(true);
    try {
      const res: any = await api.put(`/admin/users/${userId}`, { role: newRole });
      const d = res?.data || res;
      if (d.success) {
        toast.success(`Role updated to ${newRole}`);
        loadDetail(userId);
        load();
      } else toast.error(d.message || 'Failed to update role');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update role');
    } finally { setUpdatingRole(false); }
  };

  const updateUserPermissions = async (userId: string, permissions: Record<string, boolean>) => {
    setUpdatingPermissions(true);
    try {
      const res: any = await api.put(`/admin/users/${userId}`, { permissions });
      const d = res?.data || res;
      if (d.success) {
        toast.success('Permissions updated');
        loadDetail(userId);
      } else toast.error(d.message || 'Failed to update permissions');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update permissions');
    } finally { setUpdatingPermissions(false); }
  };

  const signInAsUser = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Sign in as ${userEmail}? You will be viewing their portal session. This is fully audit-logged.`)) return;
    setImpersonating(true);
    try {
      const ok = await loginAs(userId, userEmail);
      if (ok) navigate('/');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to sign in as user');
    } finally { setImpersonating(false); }
  };

  const deleteUser = async (userId: string, name: string) => {
    // A hard delete, possible only for someone with no recorded history; the
    // server answers 409 with what to do instead (userDeletionCopy.ts).
    if (!window.confirm(deleteUserConfirmText(name))) return;
    setDeletingUser(true);
    try {
      const res: any = await api.delete(`/admin/users/${userId}`);
      const d = res?.data || res;
      if (d.success) {
        toast.success(d.message || 'User deleted');
        setSelectedUser(null);
        setUserDetail(null);
        load();
      } else toast.error(d.message || 'Failed to delete user');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete user');
    } finally { setDeletingUser(false); }
  };

  // Detail view
  if (selectedUser) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Users
          </Button>
        </div>

        {detailLoading ? (
          <div className="text-center py-16 text-muted-foreground">Loading user details...</div>
        ) : userDetail ? (
          <>
            {/* User Header */}
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
                      <User className="h-7 w-7 text-muted-foreground" />
                    </div>
                    <div>
                      <h1 className="text-xl font-display font-semibold text-[#242424]">
                        {[userDetail.first_name, userDetail.last_name].filter(Boolean).join(' ') || userDetail.email}
                      </h1>
                      <p className="text-sm text-muted-foreground">{userDetail.email}</p>
                      {userDetail.phone && <p className="text-xs text-muted-foreground">{userDetail.phone}</p>}
                      <p className="text-xs text-muted-foreground mt-1">Joined {fmtDate(userDetail.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {kycBadge(userDetail.kyc_status)}
                    <Badge variant="secondary" className="text-xs">{userDetail.role}</Badge>
                    {userDetail.kyc_status !== 'verified' && (
                      <Button size="sm" variant="outline" onClick={() => sendKycReminder(userDetail.id)} disabled={sendingKyc}>
                        <Send className="h-3 w-3 mr-1" /> {sendingKyc ? 'Sending...' : 'Send KYC Reminder'}
                      </Button>
                    )}
                    {userDetail.accreditation_status !== 'verified' && userDetail.accreditation_status !== 'self_declared_non_accredited' && (
                      <Button size="sm" variant="outline" onClick={() => sendAccreditationNudge(userDetail.id)} disabled={sendingAccreditation}>
                        <FileUp className="h-3 w-3 mr-1" /> {sendingAccreditation ? 'Sending...' : 'Send Accreditation Request'}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => signInAsUser(userDetail.id, userDetail.email)} disabled={impersonating} className="border-blue-300 text-blue-700 hover:bg-blue-50">
                      <LogIn className="h-3 w-3 mr-1" /> {impersonating ? 'Signing in...' : 'Sign In As'}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteUser(userDetail.id, personName(userDetail))} disabled={deletingUser}>
                      <Trash2 className="h-3 w-3 mr-1" /> {deletingUser ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Status Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">KYC</span>
                  </div>
                  <p className="text-sm font-semibold text-[#242424] capitalize">{userDetail.kyc_status}</p>
                  {userDetail.kyc_verified_at && <p className="text-xs text-muted-foreground">{fmtDate(userDetail.kyc_verified_at)}</p>}
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Bank Accounts</span>
                  </div>
                  <p className="text-sm font-semibold text-[#242424]">{userDetail.bank_accounts?.length || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Wallet className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Crypto Wallet</span>
                  </div>
                  <p className="text-sm font-semibold text-[#242424]">{userDetail.wallet_address ? 'Connected' : 'Not linked'}</p>
                  {userDetail.wallet_address && (
                    <p className="text-xs text-muted-foreground font-mono truncate">{userDetail.wallet_address}</p>
                  )}
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Email</span>
                  </div>
                  <p className="text-sm font-semibold text-[#242424]">{userDetail.email_verified ? 'Verified' : 'Unverified'}</p>
                </CardContent>
              </Card>
            </div>

            {/* Investor Access Review — offering-document access, investment
                eligibility, Rule 506 rationale. Every field on it is a
                securities-workflow field. An eMerge administrator opening a
                nurse's record saw it in full. */}
            {TENANT.features.investing && (
            <Card className="border-border">
              <CardContent className="p-5 space-y-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                      <h3 className="font-semibold text-[#242424]">Investor Access Review</h3>
                    </div>
                    <p className="text-sm text-muted-foreground max-w-3xl">
                      Approving document access allows the investor to review offering materials. KYC, accreditation, suitability, and investment execution remain separately tracked.
                    </p>
                    {userDetail.reviewed_at && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Last reviewed {fmtDateTime(userDetail.reviewed_at)} {userDetail.reviewer_id ? `by ${userDetail.reviewer_id}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {accessBadge(userDetail.manual_review_status)}
                    {accessBadge(userDetail.document_access_status)}
                    {accessBadge(userDetail.investment_eligibility_status)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Manual Review Status</label>
                    <div className="mt-1">{accessBadge(userDetail.manual_review_status)}</div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Document Access Status</label>
                    <div className="mt-1">{accessBadge(userDetail.document_access_status)}</div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Investment Eligibility Status</label>
                    <div className="mt-1">{accessBadge(userDetail.investment_eligibility_status)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground">Relationship Source</label>
                    <Input
                      value={accessReviewForm.relationshipSource}
                      onChange={e => setAccessReviewForm(s => ({ ...s, relationshipSource: e.target.value }))}
                      placeholder="Pre-existing relationship, referral, event intro"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Known By</label>
                    <Input
                      value={accessReviewForm.knownBy}
                      onChange={e => setAccessReviewForm(s => ({ ...s, knownBy: e.target.value }))}
                      placeholder={`${TENANT.branding?.name || 'Platform'} leadership, partner, event`}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Approval Rationale</label>
                    <textarea
                      value={accessReviewForm.rationale}
                      onChange={e => setAccessReviewForm(s => ({ ...s, rationale: e.target.value }))}
                      className="mt-1 min-h-[92px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Why document access is being approved or changed"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Supporting Notes</label>
                    <textarea
                      value={accessReviewForm.reviewNotes}
                      onChange={e => setAccessReviewForm(s => ({ ...s, reviewNotes: e.target.value }))}
                      className="mt-1 min-h-[92px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Call notes, context, requested follow-up"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-muted-foreground">Compliance Caveat</label>
                    <Input
                      value={accessReviewForm.complianceCaveat}
                      onChange={e => setAccessReviewForm(s => ({ ...s, complianceCaveat: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>

                {userDetail.investor_access_reviews?.length > 0 && (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <div className="bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">Recent Access Review History</div>
                    <div className="divide-y divide-border">
                      {userDetail.investor_access_reviews.slice(0, 3).map((review: any) => (
                        <div key={review.id} className="p-3 text-xs text-muted-foreground flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                          <span>{statusLabel(review.action_type)} by {review.admin_actor_email || 'admin'}</span>
                          <span>{statusLabel(review.previous_document_access_status)} to {statusLabel(review.new_document_access_status)} · {fmtDateTime(review.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => submitInvestorAccessReview('approve_document_access')} disabled={updatingAccessReview} className="bg-green-600 hover:bg-green-700 text-white">
                    <CheckCircle className="h-3 w-3 mr-1" /> Approve Document Access
                  </Button>
                  <Button
                    onClick={() => submitInvestorAccessReview('approve_investment')}
                    disabled={updatingAccessReview || userDetail.investment_eligibility_status === 'eligible_to_invest'}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    {userDetail.investment_eligibility_status === 'eligible_to_invest' ? 'Eligible to Invest ✓' : 'Approve Investment Eligibility'}
                  </Button>
                  <Button onClick={() => submitInvestorAccessReview('legacy_manual_review_approved')} disabled={updatingAccessReview} variant="outline">
                    <ShieldCheck className="h-3 w-3 mr-1" /> Legacy Manual Review Approved
                  </Button>
                  <Button onClick={() => submitInvestorAccessReview('needs_more_information')} disabled={updatingAccessReview} variant="outline">
                    <AlertCircle className="h-3 w-3 mr-1" /> Needs More Information
                  </Button>
                  <Button onClick={() => submitInvestorAccessReview('reject')} disabled={updatingAccessReview} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50">
                    <ShieldX className="h-3 w-3 mr-1" /> Reject
                  </Button>
                  <Button onClick={() => submitInvestorAccessReview('suspend_document_access')} disabled={updatingAccessReview} variant="outline" className="border-orange-200 text-orange-700 hover:bg-orange-50">
                    Suspend Document Access
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Accreditation Status — accredited-investor status under
                Regulation D. Not a thing a hearing-care employee has. */}
            {TENANT.features.investing && (
            <Card className="border-border">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <FileUp className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-semibold text-[#242424]">Accreditation</span>
                    </div>
                    <p className="text-sm capitalize text-muted-foreground">
                      Status: <Badge variant="secondary" className={`text-xs ${
                        userDetail.accreditation_status === 'verified' ? 'bg-green-50 text-green-700' :
                        userDetail.accreditation_status === 'pending_review' ? 'bg-blue-50 text-blue-700' :
                        userDetail.accreditation_status === 'rejected' ? 'bg-red-50 text-red-700' :
                        userDetail.accreditation_status === 'expired' ? 'bg-orange-50 text-orange-700' :
                        userDetail.accreditation_status === 'self_declared_non_accredited' ? 'bg-purple-50 text-purple-700' :
                        userDetail.accreditation_status === 'self_declared_accredited' ? 'bg-sky-50 text-sky-700' :
                        'bg-amber-50 text-amber-700'
                      }`}>{userDetail.accreditation_status === 'self_declared_non_accredited' ? 'Non-Accredited' : userDetail.accreditation_status === 'self_declared_accredited' ? 'Self-Accredited' : userDetail.accreditation_status || 'pending'}</Badge>
                    </p>
                    {userDetail.accreditation_verified_at && (
                      <p className="text-xs text-muted-foreground mt-1">Verified: {fmtDate(userDetail.accreditation_verified_at)}</p>
                    )}
                    {userDetail.accreditation_expires_at && (
                      <p className="text-xs text-muted-foreground">Expires: {fmtDate(userDetail.accreditation_expires_at)}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Select
                      value={userDetail.accreditation_status || 'pending'}
                      onValueChange={(v) => updateAccreditationStatus(userDetail.id, v)}
                      disabled={updatingAccredStatus}
                    >
                      <SelectTrigger className="w-full sm:w-48"><SelectValue /></SelectTrigger>
                      <SelectContent position="item-aligned">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="self_declared_non_accredited">Non-Accredited</SelectItem>
                        <SelectItem value="self_declared_accredited">Self-Accredited</SelectItem>
                        <SelectItem value="pending_review">Pending Review</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Role & Permissions Management */}
            <Card className="border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold text-[#242424]">Role & Permissions</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">User Role</label>
                    <Select
                      value={userDetail.role || 'user'}
                      onValueChange={(v) => updateUserRole(userDetail.id, v)}
                      disabled={updatingRole}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value={MEMBER_ROLE}>{MEMBER_LABEL}</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {userDetail.role === 'admin' && (
                  <div className="mt-4 border-t pt-4">
                    <label className="text-xs text-muted-foreground mb-2 block">Admin Permissions</label>
                    <p className="text-xs text-muted-foreground mb-3">Control which admin sections this user can access.</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {([
                        { key: 'manage_users', label: 'Manage Users' },
                        { key: 'manage_offerings', label: 'Manage Offerings' },
                        { key: 'manage_properties', label: 'Manage Properties' },
                        { key: 'manage_transactions', label: 'Manage Transactions' },
                        { key: 'manage_engagements', label: 'Manage Engagements' },
                        { key: 'manage_page_builder', label: 'Page Builder' },
                        { key: 'manage_documents', label: 'Documents' },
                        { key: 'manage_settings', label: 'Platform Settings' },
                      ]).map(({ key, label }) => {
                        const perms = userDetail.permissions || {};
                        const checked = perms[key] !== false;
                        return (
                          <label key={key} className="flex items-center gap-2 p-2 rounded bg-gray-50 border border-gray-200 text-sm cursor-pointer hover:bg-gray-100">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={updatingPermissions}
                              onChange={() => {
                                const updated = { ...perms, [key]: !checked };
                                updateUserPermissions(userDetail.id, updated);
                              }}
                              className="h-4 w-4 accent-emerald-600"
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bank Accounts */}
            {userDetail.bank_accounts?.length > 0 && (
              <Card className="border-border">
                <CardContent className="p-0 overflow-x-auto">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-semibold text-[#242424]">Bank Accounts</h3>
                  </div>
                  <table className="w-full min-w-[500px]">
                    <thead><tr className="border-b border-border">
                      <th className="text-left p-3 text-xs text-muted-foreground">Institution</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Last 4</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Type</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Status</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Linked</th>
                    </tr></thead>
                    <tbody>
                      {userDetail.bank_accounts.map((b: any) => (
                        <tr key={b.id} className="border-b border-border">
                          <td className="p-3 text-sm">{b.institution_name || '—'}</td>
                          <td className="p-3 text-sm font-mono">••{b.last4 || '????'}</td>
                          <td className="p-3 text-sm capitalize">{b.account_type || '—'}</td>
                          <td className="p-3"><Badge variant="secondary" className="text-xs bg-green-50 text-green-700">{b.status}</Badge></td>
                          <td className="p-3 text-sm text-muted-foreground">{fmtDate(b.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Transactions */}
            <Card className="border-border">
              <CardContent className="p-0">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold text-[#242424]">Transactions ({userDetail.transactions?.length || 0})</h3>
                </div>
                {userDetail.transactions?.length > 0 ? (
                  <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead><tr className="border-b border-border">
                      <th className="text-left p-3 text-xs text-muted-foreground">TXN ID</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Engagement</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Amount</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Method</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Status</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Date</th>
                    </tr></thead>
                    <tbody>
                      {userDetail.transactions.map((t: any) => (
                        <tr key={t.id} className="border-b border-border hover:bg-muted/50">
                          <td className="p-3 text-sm font-mono">{t.txn_id}</td>
                          <td className="p-3 text-sm">{t.engagement_title || t.engagement_id || '—'}</td>
                          <td className="p-3 text-sm font-semibold">{fmtCurrency(t.amount_cents)}</td>
                          <td className="p-3 text-sm uppercase">{t.method}</td>
                          <td className="p-3">
                            <Badge variant="secondary" className={`text-xs ${
                              t.status === 'completed' || t.status === 'settled' ? 'bg-green-50 text-green-700' :
                              t.status === 'processing' ? 'bg-blue-50 text-blue-700' :
                              t.status === 'failed' ? 'bg-red-50 text-red-700' :
                              'bg-gray-50 text-gray-600'
                            }`}>{t.status}</Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{fmtDate(t.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground text-sm">No transactions yet</div>
                )}
              </CardContent>
            </Card>

            {/* Investments */}
            {userDetail.investments?.length > 0 && (
              <Card className="border-border">
                <CardContent className="p-0 overflow-x-auto">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-semibold text-[#242424]">Investments ({userDetail.investments.length})</h3>
                  </div>
                  <table className="w-full min-w-[500px]">
                    <thead><tr className="border-b border-border">
                      <th className="text-left p-3 text-xs text-muted-foreground">Deal</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Amount</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Method</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Status</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Date</th>
                    </tr></thead>
                    <tbody>
                      {userDetail.investments.map((inv: any) => (
                        <tr key={inv.id} className="border-b border-border hover:bg-muted/50">
                          <td className="p-3 text-sm">{inv.deal_title || '—'}</td>
                          <td className="p-3 text-sm font-semibold">{fmtCurrency(inv.amount_cents)}</td>
                          <td className="p-3 text-sm uppercase">{inv.method}</td>
                          <td className="p-3">
                            <Badge variant="secondary" className={`text-xs ${
                              inv.status === 'funded' ? 'bg-green-50 text-green-700' :
                              inv.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                              'bg-gray-50 text-gray-600'
                            }`}>{inv.status}</Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{fmtDate(inv.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Documents */}
            <Card className="border-border">
              <CardContent className="p-0">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold text-[#242424]">Documents ({userDetail.documents?.length || 0})</h3>
                </div>
                {userDetail.documents?.length > 0 ? (
                  <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead><tr className="border-b border-border">
                      <th className="text-left p-3 text-xs text-muted-foreground">Document</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Folder</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Signed</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Signed At</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Size</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Actions</th>
                    </tr></thead>
                    <tbody>
                      {userDetail.documents.map((doc: any) => (
                        <tr key={doc.id} className="border-b border-border hover:bg-muted/50">
                          <td className="p-3 text-sm flex items-center gap-2">
                            <FileText className="h-3 w-3 text-muted-foreground" />
                            {doc.title || doc.original_filename || '—'}
                          </td>
                          <td className="p-3 text-sm capitalize text-muted-foreground">{doc.folder || '—'}</td>
                          <td className="p-3">
                            {doc.is_signed_copy ? (
                              <Badge variant="secondary" className="text-xs bg-green-50 text-green-700">
                                <CheckCircle className="h-3 w-3 mr-1" />Signed
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Original</Badge>
                            )}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{fmtDateTime(doc.signed_at)}</td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {doc.file_size_bytes ? `${Math.round(doc.file_size_bytes / 1024)} KB` : '—'}
                          </td>
                          <td className="p-3">
                            <Button size="sm" variant="ghost" onClick={async () => {
                              try {
                                const r: any = await api.get(`/admin/documents/${doc.id}/download`);
                                const url = (r as any)?.url || (r as any)?.data?.url;
                                if (url) window.open(url, '_blank');
                                else toast.error('No download URL');
                              } catch { toast.error('Download failed'); }
                            }}>
                              <Download className="h-3 w-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground text-sm">No documents yet</div>
                )}
              </CardContent>
            </Card>

            {/* Accreditation Documents */}
            <Card className="border-border">
              <CardContent className="p-0">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold text-[#242424]">Accreditation Documents ({accreditationDocs.length})</h3>
                </div>
                {accreditationDocs.length > 0 ? (
                  <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead><tr className="border-b border-border">
                      <th className="text-left p-3 text-xs text-muted-foreground">File</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Type</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Size</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Uploaded</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Actions</th>
                    </tr></thead>
                    <tbody>
                      {accreditationDocs.map((doc: any) => (
                        <tr key={doc.id} className="border-b border-border hover:bg-muted/50">
                          <td className="p-3 text-sm flex items-center gap-2">
                            <FileText className="h-3 w-3 text-muted-foreground" />
                            {doc.title || doc.original_filename || '—'}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground capitalize">{doc.accreditation_type || doc.category || '—'}</td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {doc.file_size_bytes ? `${Math.round(doc.file_size_bytes / 1024)} KB` : '—'}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{fmtDate(doc.created_at)}</td>
                          <td className="p-3">
                            <Button size="sm" variant="ghost" onClick={async () => {
                              try {
                                const r: any = await api.get(`/accreditation/admin/users/${userDetail.id}/docs/${encodeURIComponent(doc.key || doc.id)}/download`);
                                const url = (r as any)?.url || (r as any)?.data?.url;
                                if (url) window.open(url, '_blank');
                                else toast.error('No download URL');
                              } catch { toast.error('Download failed'); }
                            }}>
                              <Download className="h-3 w-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground text-sm">No accreditation documents uploaded yet</div>
                )}
              </CardContent>
            </Card>

            {/* E-Signatures */}
            {userDetail.signatures?.length > 0 && (
              <Card className="border-border">
                <CardContent className="p-0">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-semibold text-[#242424]">E-Signatures ({userDetail.signatures.length})</h3>
                  </div>
                  <table className="w-full min-w-[400px]">
                    <thead><tr className="border-b border-border">
                      <th className="text-left p-3 text-xs text-muted-foreground">Document</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Status</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Sent</th>
                      <th className="text-left p-3 text-xs text-muted-foreground">Signed</th>
                    </tr></thead>
                    <tbody>
                      {userDetail.signatures.map((s: any) => (
                        <tr key={s.id} className="border-b border-border hover:bg-muted/50">
                          <td className="p-3 text-sm flex items-center gap-2"><FileText className="h-3 w-3 text-muted-foreground" />{s.template_name || '—'}</td>
                          <td className="p-3">
                            <Badge variant="secondary" className={`text-xs ${
                              s.status === 'signed' ? 'bg-green-50 text-green-700' :
                              s.status === 'viewed' ? 'bg-amber-50 text-amber-700' :
                              'bg-blue-50 text-blue-700'
                            }`}>{s.status}</Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{fmtDate(s.sent_at)}</td>
                          <td className="p-3 text-sm text-muted-foreground">{fmtDate(s.completed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </>
        ) : null}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[#242424]">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {TENANT.features.investing
              ? 'Manage platform users, KYC status, and activity'
              : 'Manage platform users, account activation, and roles'}
          </p>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 w-full sm:w-auto">
          <Button onClick={() => setShowCreateModal('member')} className="justify-center bg-blue-600 hover:bg-blue-700 text-white">
            <UserPlus className="h-4 w-4 mr-1" /> Add {MEMBER_LABEL}
          </Button>
          <Button onClick={() => setShowCreateModal('admin')} className="justify-center bg-emerald-600 hover:bg-emerald-700 text-white">
            <Shield className="h-4 w-4 mr-1" /> Add Admin
          </Button>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <Card className="border-border border-2 border-emerald-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#242424] flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Add {showCreateModal === 'admin' ? 'Admin' : MEMBER_LABEL} User
              </h3>
              <Button size="sm" variant="ghost" onClick={() => { setShowCreateModal(null); setCreateForm({ email: '', firstName: '', lastName: '', password: '', accreditationStatus: 'pending' }); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground">Email *</label>
                <Input
                  value={createForm.email}
                  onChange={e => setCreateForm(s => ({ ...s, email: e.target.value }))}
                  placeholder="user@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">First Name</label>
                <Input
                  value={createForm.firstName}
                  onChange={e => setCreateForm(s => ({ ...s, firstName: e.target.value }))}
                  placeholder="John"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Last Name</label>
                <Input
                  value={createForm.lastName}
                  onChange={e => setCreateForm(s => ({ ...s, lastName: e.target.value }))}
                  placeholder="Doe"
                  className="mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground">Password (optional — random if blank)</label>
                <Input
                  type="password"
                  value={createForm.password}
                  onChange={e => setCreateForm(s => ({ ...s, password: e.target.value }))}
                  placeholder="Min 8 characters"
                  className="mt-1"
                />
              </div>
              {showCreateModal === 'member' && TENANT.features.investing && (
                <div>
                  <label className="text-xs text-muted-foreground">Accreditation Status</label>
                  <Select
                    value={createForm.accreditationStatus}
                    onValueChange={(v) => setCreateForm(s => ({ ...s, accreditationStatus: v }))}
                  >
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="self_declared_non_accredited">Non-Accredited</SelectItem>
                      <SelectItem value="self_declared_accredited">Self-Accredited</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-end md:col-span-1">
                <Button onClick={createUser} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {creating ? 'Creating...' : `Create ${showCreateModal === 'admin' ? 'Admin' : MEMBER_LABEL}`}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-[#242424]">{pagination?.total || users.length}</p>
            <p className="text-xs text-muted-foreground">Total Users</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-600">{users.filter(u => u.kyc_status === 'verified').length}</p>
            <p className="text-xs text-muted-foreground">KYC Verified</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-600">{users.filter(u => u.kyc_status === 'pending').length}</p>
            <p className="text-xs text-muted-foreground">KYC Pending</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-600">{users.filter(u => u.document_access_status === 'approved').length}</p>
            <p className="text-xs text-muted-foreground">Docs Approved</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-600">{users.filter(u => u.wallet_address).length}</p>
            <p className="text-xs text-muted-foreground">Wallet Linked</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_auto] gap-3 sm:items-center">
        <div className="relative min-w-0 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-36"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value={MEMBER_ROLE}>{MEMBER_LABEL}</SelectItem>
            <SelectItem value="user">User</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kycFilter} onValueChange={v => { setKycFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-36"><SelectValue placeholder="KYC" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All KYC</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 text-xs text-muted-foreground">User</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Role</th>
                <th className="text-left p-4 text-xs text-muted-foreground">KYC</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Document Access</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Bank</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Wallet</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Transactions</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Joined</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u: any) => (
                <tr
                  key={u.id}
                  className="border-b border-border hover:bg-muted/50 cursor-pointer"
                  onClick={() => openUser(u)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-[#242424]">
                          {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                        </div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <Badge variant="secondary" className="text-xs capitalize">{u.role}</Badge>
                  </td>
                  <td className="p-4">{kycBadge(u.kyc_status)}</td>
                  <td className="p-4">{accessBadge(u.document_access_status)}</td>
                  <td className="p-4">
                    {Number(u.bank_accounts_count) > 0 ? (
                      <Badge variant="secondary" className="text-xs bg-green-50 text-green-700">
                        <Building2 className="h-3 w-3 mr-1" />{u.bank_accounts_count}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-4">
                    {u.wallet_address ? (
                      <Badge variant="secondary" className="text-xs bg-purple-50 text-purple-700">
                        <Wallet className="h-3 w-3 mr-1" />Linked
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-[#242424]">{u.transactions_count || 0}</div>
                    {Number(u.total_transaction_cents) > 0 && (
                      <div className="text-xs text-muted-foreground">{fmtCurrency(Number(u.total_transaction_cents))}</div>
                    )}
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">{fmtDate(u.created_at)}</td>
                  <td className="p-4" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      {u.kyc_status !== 'verified' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => sendKycReminder(u.id)}
                          title="Send KYC reminder"
                          disabled={sendingKyc}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteUser(u.id, personName(u))}
                        title="Permanently delete user (only someone with no recorded history)"
                        disabled={deletingUser}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && !loading && (
                <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">No users found</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * 50 + 1}–{Math.min(page * 50, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={!pagination.hasPrev} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button size="sm" variant="outline" disabled={!pagination.hasNext} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
